import type React from 'react';

/**
 * Automagically smooth-scrolls to the given react reference
 *
 * @param ref the element to scroll to
 * @param afterTimeout whether the scroll should occur after a timeout, this is needed since
 * subsequent calls to scroll by other elements on the page can cancel this call to scroll
 * if it has not completed scrolling.
 */
function scrollToRef<T extends HTMLElement>(
  ref: React.MutableRefObject<T>,
  afterTimeout = false
): void {
  if (ref) {
    function scroll() {
      ref.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }
    if (afterTimeout) {
      setTimeout(() => {
        scroll();
      });
    } else {
      scroll();
    }
  }
}

/**
 * Get the scroll parent of an element. Based on
 *
 * https://stackoverflow.com/a/42543908/646585
 *
 * @param element the element to find the scroll parent for
 * @param includeHidden whether to include a check if the parent element's overflow is set to hidden.
 * @param localDocument the document to use, can be overridden when used in an iframe
 */
function getScrollParent(
  element: HTMLElement,
  includeHidden = false,
  localDocument: Document = document
): HTMLElement {
  let style = getComputedStyle(element);
  const excludeStaticParent = style.position === 'absolute';
  const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

  if (style.position === 'fixed') {
    return localDocument.body;
  }

  for (let parent = element; (parent = parent.parentElement); ) {
    style = getComputedStyle(parent);

    if (excludeStaticParent && style.position === 'static') {
      continue;
    }

    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
      return parent;
    }
  }

  return null;
}

export { scrollToRef, getScrollParent };
